import {
  ExamLocationRequestApprovalType,
  FilterOperator,
  useAdminSearchRoomByLocationNameQuery,
  useAdminGetExamRoomByIdQuery,
} from '@exam-center/graphql';
import { APP_PERMISSIONS } from '@exam-center/permission';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
  DialogActions,
  Flex,
  Text,
  DescriptionOutlinedIcon,
  PlaceOutlinedIcon,
  FormRadio,
  FormLabel,
  LabelColor,
  Label,
  EditOutlinedIcon,
  IconButton,
  CloseIcon,
  SearchInput,
  useTheme,
} from '@shared/components';
import { useDisclosure, useMediaQuery } from '@shared/hooks';
import { WithPermission } from '@shared/permissions';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { useExamLocationGrantAccessRoomListSearchParams } from '../../hooks';
import {
  useAdminExamRoomRequestInfo,
  useExamCenterLocationRequestVerdictFormContext,
} from '../../providers';
import { type AdminExamRoomRequestInfoGrantAccessRoom } from '../../types';
import { AdminExamLocationManagementGrantAccessRoomList } from '../admin-exam-location-management-grant-access-room-list';

interface ManageExamRoomRequestRightsModalProps {
  isOpen: boolean;
  onClose: () => void;
  setExamRoomRequestInfoGrantAccessRoom: (
    room: AdminExamRoomRequestInfoGrantAccessRoom | null,
  ) => void;
}

const STATUS_CONFIGS: Record<
  ExamLocationRequestApprovalType,
  { label: string; color: LabelColor }
> = {
  [ExamLocationRequestApprovalType.GrantAccess]: {
    label: 'เปิดสิทธิ์',
    color: LabelColor.WARNING,
  },
  [ExamLocationRequestApprovalType.New]: {
    label: 'อนุมัติใหม่',
    color: LabelColor.INFO,
  },
};

function ManageExamRoomRequestRightsModal(props: Readonly<ManageExamRoomRequestRightsModalProps>) {
  const { isOpen, onClose, setExamRoomRequestInfoGrantAccessRoom } = props;

  const { examLocationRequestInfo, examRoomRequestInfo } = useAdminExamRoomRequestInfo();
  const [isConfirmButtonEnable, setIsConfirmButtonEnable] = useState(false);
  const [{ search, page, pageSize }, { changeSearch }] =
    useExamLocationGrantAccessRoomListSearchParams();
  const theme = useTheme();

  const { data: adminSearchRoomByLocationName, loading } = useAdminSearchRoomByLocationNameQuery({
    variables: {
      adminSearchRoomByLocationNameInput: {
        paging: {
          pageSize,
          currentPage: page,
        },
        filter: {
          conditions: [
            {
              field: 'status',
              operator: FilterOperator.Equal,
              value: 'ACTIVE',
            },
          ],
        },
        locationName: search,
        provinceId: examRoomRequestInfo?.examLocationRequestInfo?.provinceId,
      },
    },
  });
  const parentForm = useExamCenterLocationRequestVerdictFormContext();

  const modalForm = useForm({
    defaultValues: {
      approvalType: parentForm.getValues('approvalType'),
      grantedRoomId: parentForm.getValues('grantedRoomId'),
    },
  });

  useEffect(() => {
    if (isOpen) {
      modalForm.reset({
        approvalType:
          parentForm.getValues('approvalType') ?? ExamLocationRequestApprovalType.GrantAccess,
        grantedRoomId: parentForm.getValues('grantedRoomId'),
      });
    }
  }, [isOpen, modalForm, parentForm]);

  const approvalType = modalForm.watch('approvalType');
  const grantedRoomId = modalForm.watch('grantedRoomId');

  useEffect(() => {
    if (approvalType === ExamLocationRequestApprovalType.New) {
      setIsConfirmButtonEnable(true);
    } else if (approvalType === ExamLocationRequestApprovalType.GrantAccess) {
      setIsConfirmButtonEnable(!!grantedRoomId);
    }
  }, [approvalType, modalForm, grantedRoomId]);

  const handleSelectRow = (selectedRoom: string | undefined) => {
    if (selectedRoom) {
      modalForm.setValue('grantedRoomId', selectedRoom, {
        shouldDirty: true,
      });
    }
  };

  const handleConfirm = () => {
    const approvalType = modalForm.getValues('approvalType');
    const grantedRoomId = modalForm.getValues('grantedRoomId');
    const roomData = adminSearchRoomByLocationName?.ecAdminSearchRoomByLocationName.rooms.find(
      (room) => room.id === grantedRoomId,
    );
    parentForm.setValue('approvalType', modalForm.getValues('approvalType'), {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    parentForm.setValue('grantedRoomId', modalForm.getValues('grantedRoomId'), {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    setExamRoomRequestInfoGrantAccessRoom(
      approvalType === ExamLocationRequestApprovalType.GrantAccess
        ? {
            province: roomData?.building.location.province.nameTh ?? '-',
            location: {
              locationName: roomData?.building.location.nameTh ?? '-',
              buildingName: roomData?.building.nameTh ?? '-',
              floorName: roomData?.floor ?? '-',
            },
            room: {
              roomName: roomData?.name ?? '-',
              roomNumber: roomData?.roomNumber ?? '-',
            },
          }
        : null,
    );
    onClose();
  };
  const isDesktop = useMediaQuery(theme.breakpoints.up('desktop'));

  return (
    <FormProvider {...modalForm}>
      <Dialog open={isOpen} onClose={onClose} maxWidth='xl' fullScreen={!isDesktop}>
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid',
            borderColor: theme.palette.divider,
          }}
        >
          จัดการสิทธิ์ใช้งานสถานที่
          <IconButton onClick={() => onClose()}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ mt: 6 }}>
          <Flex sx={{ flexDirection: 'column', gap: 6 }}>
            <Flex
              sx={(theme) => ({
                gap: 2,
                border: '1px solid',
                borderColor: theme.palette.divider,
                backgroundColor: theme.palette.grey[50],
                borderRadius: theme.shape.rounded(2),
                p: 4,
              })}
            >
              <PlaceOutlinedIcon
                sx={(theme) => ({ fontSize: '24px', color: theme.palette.text.secondary })}
              />
              <Flex sx={{ flexDirection: 'column', gap: 2 }}>
                <Text variant='body1Semibold'>{examLocationRequestInfo?.nameTh}</Text>
                <Text variant='body1'>
                  อาคาร: {examLocationRequestInfo?.buildingName ?? '-'} ชั้น:{' '}
                  {examRoomRequestInfo.floor} ห้อง: {examRoomRequestInfo.name ?? '-'} เลขห้อง:{' '}
                  {examRoomRequestInfo.roomNumber ?? '-'}
                </Text>
              </Flex>
            </Flex>
            <Text variant='h4'>อนุมัติสิทธิ์ใช้งานสถานที่</Text>
            <Flex
              sx={(theme) => ({
                flexDirection: 'column',
                gap: 2,
                border: '1px solid',
                borderColor: theme.palette.divider,
                backgroundColor: theme.palette.grey[50],
                borderRadius: theme.shape.rounded(2),
                p: 4,
              })}
            >
              <FormLabel>เลือกอนุมัติสิทธิ์ใช้งานสถานที่</FormLabel>
              <Flex>
                <FormRadio
                  name='approvalType'
                  value={ExamLocationRequestApprovalType.GrantAccess}
                  label='เปิดสิทธิ์'
                />
                <FormRadio
                  name='approvalType'
                  value={ExamLocationRequestApprovalType.New}
                  label='อนุมัติใหม่'
                />
              </Flex>
            </Flex>
            {approvalType === ExamLocationRequestApprovalType.GrantAccess ? (
              <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(4) }}>
                <Text variant='h4'>เลือกสถานที่สอบ (เลือกได้ 1 สถานที่)</Text>
                <SearchInput
                  defaultValue={search}
                  placeholder='ค้นหาชื่อสถานที่สอบ...'
                  onChange={changeSearch}
                />

                <AdminExamLocationManagementGrantAccessRoomList
                  roomId={grantedRoomId}
                  onSelectRow={handleSelectRow}
                  loading={loading}
                  adminSearchRoomByLocationName={adminSearchRoomByLocationName}
                />
              </Flex>
            ) : null}
          </Flex>
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button size='large' variant='outlined' color='secondary' onClick={onClose}>
            ยกเลิก
          </Button>
          <Button
            size='large'
            variant='contained'
            onClick={handleConfirm}
            disabled={!isConfirmButtonEnable}
          >
            บันทึก
          </Button>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
}

export default function AdminManageExamRoomRequestRightsModal() {
  const [isUsageRightsModalOpen, { open, close }] = useDisclosure();

  const formContext = useExamCenterLocationRequestVerdictFormContext();
  const approvalType = formContext.watch('approvalType');
  const grantedRoomId = formContext.watch('grantedRoomId');
  const { examLocationRequestInfo, examRoomRequestInfo } = useAdminExamRoomRequestInfo();
  const [, { changeSearch, changePageNumber, changePageSize }] =
    useExamLocationGrantAccessRoomListSearchParams();
  const [examRoomRequestInfoGrantAccessRoom, setExamRoomRequestInfoGrantAccessRoom] =
    useState<AdminExamRoomRequestInfoGrantAccessRoom | null>(null);

  const { data: examRoom } = useAdminGetExamRoomByIdQuery({
    skip: !grantedRoomId,
    variables: {
      getExamRoomByIdInput: {
        id: grantedRoomId || '',
      },
    },
  });

  const handleClose = () => {
    formContext.resetField('approvalType');
    formContext.resetField('grantedRoomId');
    changeSearch('');
    changePageNumber(0);
    changePageSize(10);
    close();
  };
  const approvalTypeFieldError = formContext.formState.errors?.approvalType;
  const approvalTypeErrorMessage =
    (typeof approvalTypeFieldError?.message === 'string' && approvalTypeFieldError.message) ||
    'กรุณาจัดการสิทธิ์การใช้งานสถานที่';

  return (
    <>
      <Flex sx={{ flexDirection: 'column', gap: 2 }}>
        <Text variant='body2Semibold'>
          จัดการสิทธิ์ใช้งานสถานที่{' '}
          <Text component='span' color='error'>
            *
          </Text>
        </Text>
        {approvalType ? (
          <Flex
            sx={(theme) => ({
              p: 4,
              width: '100%',
              flexDirection: 'row',
              gap: 2,
              alignItems: 'center',
              justifyContent: 'space-between',
              borderRadius: theme.shape.rounded(2),
              border: '1px solid',
              borderColor: theme.palette.divider,
            })}
          >
            {approvalType === ExamLocationRequestApprovalType.GrantAccess && grantedRoomId ? (
              <Flex sx={{ alignItems: 'center', gap: 4, width: '100%' }}>
                <Flex sx={{ flexDirection: 'column', gap: 2, flex: 1 }}>
                  <Text variant='body1Semibold'>
                    {examRoomRequestInfoGrantAccessRoom?.location.locationName ??
                      examRoom?.ecAdminGetExamRoomById.examRoom.building.location.nameTh ??
                      '-'}
                  </Text>
                  <Text variant='body1'>
                    อาคาร:{' '}
                    {examRoomRequestInfoGrantAccessRoom?.location.buildingName ??
                      examRoom?.ecAdminGetExamRoomById.examRoom.building.nameTh ??
                      '-'}{' '}
                    ชั้น:{' '}
                    {examRoomRequestInfoGrantAccessRoom?.location.floorName ??
                      examRoom?.ecAdminGetExamRoomById.examRoom.floor ??
                      '-'}{' '}
                    ห้อง:{' '}
                    {examRoomRequestInfoGrantAccessRoom?.room.roomName ??
                      examRoom?.ecAdminGetExamRoomById.examRoom.name ??
                      '-'}{' '}
                    เลขห้อง:{' '}
                    {examRoomRequestInfoGrantAccessRoom?.room.roomNumber ??
                      examRoom?.ecAdminGetExamRoomById.examRoom.roomNumber ??
                      '-'}
                  </Text>
                </Flex>
                <Label color={STATUS_CONFIGS[approvalType].color} size='large'>
                  {STATUS_CONFIGS[approvalType].label}
                </Label>
                <WithPermission
                  requiredPermission={[
                    APP_PERMISSIONS.ADMIN_EXAM_LOCATION_MANAGEMENT.EXAM_LOCATION_REQUEST.UPDATE,
                    APP_PERMISSIONS.ADMIN_EXAM_LOCATION_MANAGEMENT
                      .EXAM_LOCATION_REQUEST_FIRST_TIER_REVIEW.UPDATE,
                    APP_PERMISSIONS.ADMIN_EXAM_LOCATION_MANAGEMENT
                      .EXAM_LOCATION_REQUEST_SECOND_TIER_REVIEW.UPDATE,
                  ]}
                  options={{
                    operator: 'OR',
                  }}
                >
                  <Button
                    startIcon={<EditOutlinedIcon />}
                    variant='outlined'
                    onClick={() => open()}
                    color='secondary'
                    size='small'
                    sx={{ minWidth: 'fit-content' }}
                  >
                    Edit
                  </Button>
                </WithPermission>
              </Flex>
            ) : (
              <Flex sx={{ alignItems: 'center', gap: 4, width: '100%' }}>
                <Flex sx={{ flexDirection: 'column', gap: 2, flex: 1 }}>
                  <Text variant='body1Semibold'>{examLocationRequestInfo?.nameTh}</Text>
                  <Text variant='body1'>
                    อาคาร: {examLocationRequestInfo?.buildingName ?? '-'} ชั้น:{' '}
                    {examRoomRequestInfo.floor} ห้อง: {examRoomRequestInfo.name ?? '-'} เลขห้อง:{' '}
                    {examRoomRequestInfo.roomNumber ?? '-'}
                  </Text>
                </Flex>
                <Flex sx={{ alignItems: 'center', gap: 4 }}>
                  <Label color={STATUS_CONFIGS[approvalType].color} size='large'>
                    {STATUS_CONFIGS[approvalType].label}
                  </Label>
                  <WithPermission
                    requiredPermission={[
                      APP_PERMISSIONS.ADMIN_EXAM_LOCATION_MANAGEMENT.EXAM_LOCATION_REQUEST.UPDATE,
                      APP_PERMISSIONS.ADMIN_EXAM_LOCATION_MANAGEMENT
                        .EXAM_LOCATION_REQUEST_FIRST_TIER_REVIEW.UPDATE,
                      APP_PERMISSIONS.ADMIN_EXAM_LOCATION_MANAGEMENT
                        .EXAM_LOCATION_REQUEST_SECOND_TIER_REVIEW.UPDATE,
                    ]}
                    options={{
                      operator: 'OR',
                    }}
                  >
                    <Button
                      startIcon={<EditOutlinedIcon />}
                      variant='outlined'
                      onClick={() => open()}
                      color='secondary'
                      size='small'
                      sx={{ minWidth: 'fit-content' }}
                    >
                      Edit
                    </Button>
                  </WithPermission>
                </Flex>
              </Flex>
            )}
          </Flex>
        ) : (
          <>
            <Flex
              sx={(theme) => ({
                p: 4,
                width: '100%',
                flexDirection: 'column',
                gap: 2,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: theme.palette.grey[50],
                borderRadius: theme.shape.rounded(2),
                border: '1px dashed',
                borderColor: approvalTypeFieldError
                  ? theme.palette.error.main
                  : theme.palette.divider,
              })}
            >
              <DescriptionOutlinedIcon
                sx={(theme) => ({ fontSize: '48px', color: theme.palette.grey[400] })}
              />
              <Text variant='h5'>ยังไม่ได้จัดการสิทธิ์การใช้งานสถานที่</Text>
              <Button onClick={() => open()} variant='outlined'>
                จัดการสิทธิ์ใช้งาน
              </Button>
            </Flex>
            {approvalTypeFieldError ? (
              <Text variant='caption' color='error'>
                {approvalTypeErrorMessage}
              </Text>
            ) : null}
          </>
        )}
      </Flex>
      <ManageExamRoomRequestRightsModal
        setExamRoomRequestInfoGrantAccessRoom={setExamRoomRequestInfoGrantAccessRoom}
        isOpen={isUsageRightsModalOpen}
        onClose={handleClose}
      />
    </>
  );
}
